import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { StickToBottomContext } from 'use-stick-to-bottom';
import { CopyIcon } from '../../../../aida/components/svgs/icons/CopyIcon';
import {
	AIAgentTemplateFilterProperty,
	AIAgentTemplateScope,
	AIChatRole,
	AIConversationStarter,
	AIConversationType,
	CapabilityResourceConfiguration,
	CustomSurveyCapability,
	EventRegistrationCapability,
	IAIAgentTemplate,
	IAIAgentTemplateAttachment,
	IAnonymousAccessSettings,
	isTeamMeetingResource,
	MeetingCapability,
	WebSearchCapability,
} from '../../../../extViewmodels/sdk/models/AI';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { copyToClipboard } from '../../../../models/UiUtils';
import {
	useAdminAccountSuspenseQuery,
	useAIAgentTemplateFilterInfiniteSuspenseQuery,
	useInfiniteCustomSurveys,
	useInfiniteEventRegistrationSurveys,
	useSchedulerConfigQuery,
	useSchedulerConfigsQuery,
} from '../../../../queries';
import { invalidateInfiniteCustomSurveys } from '../../../../queries/Surveys/useInfiniteCustomSurveys';
import { useBrowseAllSurveyTemplatesQuery } from '../../../../queries/SurveyTemplate/useBrowseAllSurveyTemplatesQuery';
import { useTeamMeetingConfigsQuery } from '../../../../queries/TeamMeetingConfig';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { AI_AGENT_TEMPLATES_QUERY_KEY, invalidateAIAgentTemplateFilter } from '../../../../queries/AIAgentTemplate';
import { useCreateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useCreateAIAgentTemplateMutation';
import { useDeleteAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useDeleteAIAgentTemplateMutation';
import { useUpdateAIAgentTemplateMutation } from '../../../../queries/AIAgentTemplate/useUpdateAIAgentTemplateMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import {
	invalidateAllAIConversationFilterQueries,
	useAIConversationFilterSuspenseQuery,
} from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { useLoadAllUsers } from '../../../../queries/User/useLoadAllUsers';
import {
	type AssistantEmbedPosition,
	getAssistantEmbedSnippet,
	getAssistantStarterUrl,
} from '../../../../utils/assistantUrlUtils';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../web/components-v2/Popover';
import { ChevronDown } from '../../../../web/components/svgs/icons/ChevronDown';
import { CodeIcon } from '../../../../web/components/svgs/icons/CodeIcon';
import { EyeIcon } from '../../../../web/components/svgs/icons/EyeIcon';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../../../web/components/Tabs';
import { Toggle } from '../../../../web/components/Toggle';
import { UserComboboxAvatarItem } from '../../../../web/components/UserCombobox';
import { AIConversationMessage } from '../../../../web/components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../../web/components-v2/ai/ChatConversation';
import {
	PromptInput,
	PromptInputAttachmentButton,
	PromptInputBody,
	PromptInputFooter,
	PromptInputMessage,
	PromptInputSpeechButton,
	PromptInputSubmit,
	PromptInputTextarea,
	PromptInputTools,
} from '../../../../web/components-v2/ai/PromptInput';
import { Button } from '../../../../web/components-v2/Button';
import { ColorPicker } from '../../../../web/components-v2/ColorPicker';
import {
	Combobox,
	ComboboxCollection,
	ComboboxContent,
	ComboboxGroup,
	ComboboxGroupLabel,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
	ComboboxTrigger,
} from '../../../../web/components-v2/Combobox';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Input } from '../../../../web/components-v2/Input';
import {
	InputGroupV2 as InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupTextarea,
} from '../../../../web/components-v2/InputGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { TextArea } from '../../../../web/components-v2/TextArea';
import {
	AIChatAssistantMessage,
	removeErrorMessageFromConversationCache,
	useAIAssistantStreamingMutation,
} from '../../../../web/containers/templates/AssistantChat';
import { brandPrimary, charmGray } from '../../../../web/styles/colors';
import { bs } from '../../../../web/styles/styles';
import { AIAgentTemplateAttachments } from '../../AIAgentTemplates/components/AIAgentTemplateAttachments';
import { MAX_OBJECTIVE_PROMPT_LENGTH } from '../../AIAgentTemplates/constants';
import { AIAgentTemplateFormData } from '../../AIAgentTemplates/validation';
import { styles } from '../styles';
import { AgentBuilderStarterForm, AgentBuilderStarterFormSubmission } from './components/AgentBuilderStarterForm';
import { AssistantPreviewWidget } from './components/AssistantPreviewWidget';
import { AvatarImageUploader } from './components/AvatarImageUploader';
import { EmbeddedPreviewWidget } from './components/EmbeddedPreviewWidget';
import { NotepadTextDashedIcon } from '../../../../web/components/svgs/icons/NotepadTextDashedIcon';
import { NotepadTextIcon } from '../../../../web/components/svgs/icons/NotepadTextIcon';
import {
	AI_CHAT_FILE_ACCEPT,
	AI_CHAT_MAX_FILES,
	AI_CHAT_MAX_FILE_SIZE,
	PromptInputAttachments,
	useAIChatAttachments,
} from '@HoudiniUI/ai/PromptInput/PromptInputAttachments';
import { UsersIcon } from '@WebComponents/svgs/icons/UsersIcon';
import { Field, FieldLabel } from '../../../../web/components-v2/Field';

function getContrastTextColor(hexBg: string): string {
	const shorthand = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hexBg);
	if (shorthand) {
		hexBg = '#' + shorthand[1] + shorthand[1] + shorthand[2] + shorthand[2] + shorthand[3] + shorthand[3];
	}
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexBg);
	if (!result) return '#ffffff';
	const channels = [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)].map(c => {
		c = c / 255;
		return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	});
	const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
	return luminance > 0.179 ? '#000000' : '#ffffff';
}

const DEFAULT_ANONYMOUS_ACCESS: IAnonymousAccessSettings = {
	enabled: false,
	allowedOrigins: [],
};

const EMBED_POSITION_OPTIONS: { label: string; value: AssistantEmbedPosition }[] = [
	{ label: 'Bottom Right', value: 'bottom-right' },
	{ label: 'Bottom Left', value: 'bottom-left' },
];

const HTTP_PROTOCOL_REGEX = /^https?:\/\//i;

const parseHttpUrl = (value: string): URL | null => {
	try {
		const url = new URL(value);
		if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !url.hostname) {
			return null;
		}
		return url;
	} catch {
		return null;
	}
};

const normalizeHttpUrl = (value: string): string => {
	const trimmed = value.trim();
	if (!trimmed) {
		return '';
	}

	const withProtocol = HTTP_PROTOCOL_REGEX.test(trimmed) ? trimmed : `https://${trimmed}`;
	const url = parseHttpUrl(withProtocol);
	if (!url) {
		return withProtocol;
	}

	const normalizedPath = url.pathname === '/' ? '' : url.pathname.replace(/\/+$/, '');
	return `${url.protocol}//${url.host}${normalizedPath}${url.search}${url.hash}`;
};

const isValidWebSearchDomain = (domain: string): boolean => {
	return parseHttpUrl(domain) !== null;
};

const isValidEmbeddedAllowedOrigin = (origin: string): boolean => {
	const url = parseHttpUrl(origin);
	if (!url || url.protocol !== 'https:') {
		return false;
	}

	const hasPath = url.pathname !== '' && url.pathname !== '/';
	return !hasPath && !url.search && !url.hash;
};

// Helper function to convert string to AIConversationStarter with seeded chatMessages
const stringToConversationStarter = (title: string): AIConversationStarter => ({
	title,
	subTitle: undefined,
	chatMessages: title.trim() ? [{ id: null, role: AIChatRole.User, text: title }] : [],
});

function getInitialFormDataFromTemplate(template: IAIAgentTemplate): AIAgentTemplateFormData {
	let capabilities = template.capabilities ?? [];
	const hasCustomSurveyCapability = capabilities.some(c => c._type === 'CustomSurveyCapability');
	if (!hasCustomSurveyCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'CustomSurveyCapability',
				resources: [{ resourceId: '', description: '' }],
			} as CustomSurveyCapability,
		];
	}
	const hasEventCapability = capabilities.some(c => c._type === 'EventRegistrationCapability');
	if (!hasEventCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'EventRegistrationCapability',
				includeAllUpcomingEvents: true,
				resources: [{ resourceId: '' }],
			} as EventRegistrationCapability,
		];
	}
	const hasMeetingCapability = capabilities.some(c => c._type === 'MeetingCapability');
	if (!hasMeetingCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'MeetingCapability',
				resources: [{ resourceId: '', description: '' }],
			} as MeetingCapability,
		];
	}
	const hasWebSearchCapability = capabilities.some(c => c._type === 'WebSearchCapability');
	if (!hasWebSearchCapability) {
		capabilities = [
			...capabilities,
			{
				_type: 'WebSearchCapability',
				allowedDomains: [''],
			} as WebSearchCapability,
		];
	}
	return {
		name: template.name,
		description: template.description || '',
		objectivePrompt: template.objectivePrompt || '',
		technicalPrompt: template.technicalPrompt || '',
		model: template.model || '',
		conversationType: template.conversationType || '',
		selectors: template.selectors || {},
		variables: template.variables || {},
		attachments: template.attachments || [],
		agentAvatarImage: template.agentAvatarImage ?? null,
		conversationStarters: template.conversationStarters ?? [],
		anonymousAccess: template.anonymousAccess ?? DEFAULT_ANONYMOUS_ACCESS,
		capabilities,
	};
}

function getEmptyFormData(subverticals: string[] | undefined): AIAgentTemplateFormData {
	return {
		name: '',
		description: '',
		objectivePrompt: '',
		technicalPrompt: '',
		model: '',
		conversationType: Api.AIConversationType.Custom,
		selectors: {},
		variables: {},
		attachments: [],
		agentAvatarImage: null,
		conversationStarters: [],
		anonymousAccess: DEFAULT_ANONYMOUS_ACCESS,
		capabilities: [
			{
				_type: 'CustomSurveyCapability',
				resources: [{ resourceId: '', description: '' }],
			} as CustomSurveyCapability,
			{
				_type: 'EventRegistrationCapability',
				includeAllUpcomingEvents: true,
				resources: [{ resourceId: '' }],
			} as EventRegistrationCapability,
			{
				_type: 'MeetingCapability',
				resources: [{ resourceId: '', description: '' }],
			} as MeetingCapability,
			{
				_type: 'WebSearchCapability',
				allowedDomains: [
					subverticals?.find(x => x.toLowerCase().includes('medicare')) ? 'https://www.medicare.gov' : '',
				],
			} as WebSearchCapability,
		],
	};
}

interface MeetingResourceRowProps {
	index: number;
	resource: CapabilityResourceConfiguration;
	allUsers: Api.IUser[];
	isLoadingUsers: boolean;
	isSubmitting: boolean;
	canRemove: boolean;
	impersonationContext?: Api.IImpersonationContext;
	teamConfigs: Api.ITeamMeetingConfig[];
	isLoadingTeamConfigs: boolean;
	/** resourceIds picked by the other meeting rows; hidden from this row's options */
	otherSelectedResourceIds: string[];
	onResourceChange: (index: number, resource: CapabilityResourceConfiguration) => void;
	onRemove: (index: number) => void;
}

// Mirrors the backend's runtime condition for offering a team link to visitors
const isTeamConfigOffered = (config: Api.ITeamMeetingConfig) =>
	config.enabled && (config.meetingConfigs ?? []).some(member => member.enabled);

// Sentinel tier-1 entry that switches a row to team mode, shown in its own combobox group above the users
const TEAM_LINKS_OPTION = { id: '__team-meeting-links__' };
const isTeamLinksOption = (entry: unknown): boolean => (entry as { id?: string })?.id === TEAM_LINKS_OPTION.id;
type UserSelectorEntry = Api.IUser | typeof TEAM_LINKS_OPTION;
type UserSelectorGroup = { value: string; items: UserSelectorEntry[] };

function MeetingResourceRow({
	index,
	resource,
	allUsers,
	isLoadingUsers,
	isSubmitting,
	canRemove,
	impersonationContext,
	teamConfigs,
	isLoadingTeamConfigs,
	otherSelectedResourceIds,
	onResourceChange,
	onRemove,
}: MeetingResourceRowProps) {
	const isTeamRow = isTeamMeetingResource(resource);

	// State for selected user
	const [selectedUser, setSelectedUser] = React.useState<Api.IUser | null>(null);
	const [isUserPopoverOpen, setIsUserPopoverOpen] = React.useState(false);

	// Fetch existing config if we have a resourceId (edit mode); team rows resolve from teamConfigs
	const existingConfigQuery = useSchedulerConfigQuery({
		id: resource.resourceId,
		enabled: Boolean(resource.resourceId) && !selectedUser && !isTeamRow,
		impersonationContext,
	});

	// Fetch meeting configs for selected user
	const { data: userConfigsData, isLoading: isLoadingConfigs } = useSchedulerConfigsQuery({
		//userId: selectedUser?.id,
		enabled: Boolean(selectedUser) && !isTeamRow,
		impersonationContext: selectedUser
			? { ...impersonationContext, user: { id: selectedUser.id } }
			: impersonationContext,
		pageSize: 100,
	});
	const userConfigs = userConfigsData?.values ?? [];

	// Initialize selectedUser from existing config's creator (edit mode)
	React.useEffect(() => {
		if (existingConfigQuery.data && !selectedUser) {
			const creatorId = existingConfigQuery.data.creator?.id;
			if (creatorId) {
				const creator = allUsers.find(u => u.id === creatorId);
				if (creator) {
					setSelectedUser(creator);
				}
			}
		}
	}, [existingConfigQuery.data, selectedUser, allUsers]);

	// Hide configs already picked by other rows so the same link can't be added twice
	const availableUserConfigs = userConfigs.filter(c => !otherSelectedResourceIds.includes(c.id));
	const availableTeamConfigs = teamConfigs
		.filter(c => !otherSelectedResourceIds.includes(c.id))
		.sort((a, b) => (isTeamConfigOffered(a) ? 0 : 1) - (isTeamConfigOffered(b) ? 0 : 1));

	let selectedConfig: Api.IMeetingConfigResponse | Api.ITeamMeetingConfig | null = null;
	let isMissingTeamConfig = false;
	if (isTeamRow) {
		if (resource.resourceId) {
			const fromTeamConfigs = teamConfigs.find(c => c.id === resource.resourceId);
			if (fromTeamConfigs) {
				selectedConfig = fromTeamConfigs;
			} else if (!isLoadingTeamConfigs) {
				// Deleted/unknown team link: keep the raw id visible instead of an empty input
				isMissingTeamConfig = true;
				selectedConfig = { id: resource.resourceId, name: resource.resourceId } as Api.ITeamMeetingConfig;
			}
		}
	} else if (resource.resourceId) {
		const fromUserConfigs = userConfigs.find(c => c.id === resource.resourceId);
		if (fromUserConfigs) {
			selectedConfig = fromUserConfigs;
		} else if (existingConfigQuery.data?.id === resource.resourceId) {
			selectedConfig = existingConfigQuery.data;
		}
	}

	const selectedTeamConfigIsNotOffered =
		isTeamRow &&
		!isMissingTeamConfig &&
		!!selectedConfig &&
		!isTeamConfigOffered(selectedConfig as Api.ITeamMeetingConfig);

	// User button label (with fallback to creator from config)
	const userButtonLabel = React.useMemo(() => {
		if (isTeamRow) {
			return 'Team links';
		}
		if (selectedUser) {
			return getDisplayName(selectedUser);
		}
		// Fallback: try to get name from existing config's creator
		if (existingConfigQuery.data?.creator) {
			const creator = existingConfigQuery.data.creator;
			if (creator.firstName || creator.lastName) {
				return `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim();
			}
			if (creator.email) {
				return creator.email;
			}
		}
		return 'Select User';
	}, [isTeamRow, selectedUser, existingConfigQuery.data]);

	// Status message for calendar combobox
	const statusMessage = React.useMemo(() => {
		if (isTeamRow) {
			if (isLoadingTeamConfigs) return 'Loading team meeting links...';
			if (teamConfigs.length === 0) return 'No team meeting links for this account';
			if (availableTeamConfigs.length === 0) return 'All team meeting links are already selected';
			return `${availableTeamConfigs.length} team meeting link${availableTeamConfigs.length === 1 ? '' : 's'} available`;
		}
		if (!selectedUser) return 'Select a user first';
		if (isLoadingConfigs) return 'Loading calendars...';
		if (userConfigs.length === 0) return 'No calendars for this user';
		if (availableUserConfigs.length === 0) return 'All calendars for this user are already selected';
		return `${availableUserConfigs.length} calendar${availableUserConfigs.length === 1 ? '' : 's'} available`;
	}, [
		isTeamRow,
		isLoadingTeamConfigs,
		teamConfigs.length,
		availableTeamConfigs.length,
		selectedUser,
		isLoadingConfigs,
		userConfigs.length,
		availableUserConfigs.length,
	]);

	// Handle user change - clears the picked calendar and the team marker (if any)
	const handleUserChange = (user: Api.IUser | null) => {
		setSelectedUser(user);
		setIsUserPopoverOpen(false);
		onResourceChange(index, { resourceId: '', description: resource.description });
	};

	// Switch the row to team mode, clearing any picked calendar
	const handleTeamLinksSelect = () => {
		setSelectedUser(null);
		setIsUserPopoverOpen(false);
		onResourceChange(index, {
			_type: 'TeamMeetingResourceConfiguration',
			resourceId: '',
			description: resource.description,
		});
	};

	const handleConfigChange = (config: Api.IMeetingConfigResponse | Api.ITeamMeetingConfig | null) => {
		onResourceChange(index, { ...resource, resourceId: config?.id ?? '' });
	};

	const itemLabel = (item: Api.IMeetingConfigResponse | Api.ITeamMeetingConfig | null) => {
		if (!item) return '';
		if (isTeamRow) {
			return (item as Api.ITeamMeetingConfig).name ?? '';
		}
		const config = item as Api.IMeetingConfigResponse;
		return config.published?.name ?? config.draft?.name ?? '';
	};

	// Tier-1 entries grouped: the team-links switch on top, then the account's users
	const userSelectorGroups: UserSelectorGroup[] = [
		{ value: 'Team', items: [TEAM_LINKS_OPTION] },
		{ value: 'Users', items: allUsers },
	];

	return (
		<div className={css(styles.surveyRow)}>
			<div className={css(styles.surveyRowHeader)}>
				<div className={css(styles.surveyCombobox)}>
					<Combobox
						items={isTeamRow ? availableTeamConfigs : availableUserConfigs}
						value={selectedConfig}
						onValueChange={value =>
							handleConfigChange(value as Api.IMeetingConfigResponse | Api.ITeamMeetingConfig | null)
						}
						itemToStringLabel={item => itemLabel(item as Api.IMeetingConfigResponse | Api.ITeamMeetingConfig | null)}
						isItemEqualToValue={(item, value) => (item as { id: string })?.id === (value as { id: string })?.id}
					>
						<ComboboxInput
							placeholder={
								isTeamRow
									? 'Search team meeting links...'
									: selectedUser
										? 'Search calendars...'
										: 'Select a user first'
							}
							disabled={isSubmitting}
							showClear
						>
							<InputGroupAddon align='inlineStart'>
								<Combobox
									items={userSelectorGroups}
									value={isTeamRow ? TEAM_LINKS_OPTION : selectedUser}
									onValueChange={entry => {
										if (isTeamLinksOption(entry)) {
											handleTeamLinksSelect();
										} else {
											handleUserChange(entry as Api.IUser | null);
										}
									}}
									itemToStringLabel={(entry: UserSelectorEntry | null) => {
										if (!entry) return '';
										return isTeamLinksOption(entry) ? 'Team meeting links' : getDisplayName(entry as Api.IUser);
									}}
									isItemEqualToValue={(item, value) => (item as { id: string })?.id === (value as { id: string })?.id}
									open={isUserPopoverOpen}
									onOpenChange={setIsUserPopoverOpen}
								>
									<ComboboxTrigger
										render={({ className, ...props }) => (
											<InputGroupButton variant='ghost' size='xs' disabled={isSubmitting} {...props}>
												{userButtonLabel}
												<ChevronDown width={12} height={12} />
											</InputGroupButton>
										)}
									/>
									<ComboboxContent style={{ minWidth: 360, overflowX: 'hidden' }}>
										<div className={css(bs.p2)}>
											<ComboboxInput placeholder='Search users...' showTrigger={false} />
										</div>
										<ComboboxStatus>{isLoadingUsers ? 'Loading users...' : `${allUsers.length} users`}</ComboboxStatus>
										<ComboboxList styles={[bs.pt0]}>
											{(group: UserSelectorGroup) => (
												<ComboboxGroup key={group.value} items={group.items}>
													<ComboboxGroupLabel>{group.value}</ComboboxGroupLabel>
													<ComboboxCollection>
														{(entry: UserSelectorEntry) =>
															isTeamLinksOption(entry) ? (
																<ComboboxItem key={TEAM_LINKS_OPTION.id} value={entry}>
																	<ComboboxItemRadioIndicator />
																	<UsersIcon className={css(styles.surveyItemIcon)} />
																	Team meeting links
																</ComboboxItem>
															) : (
																<ComboboxItem key={(entry as Api.IUser).id} value={entry}>
																	<ComboboxItemRadioIndicator />
																	{getDisplayName(entry as Api.IUser)}
																</ComboboxItem>
															)
														}
													</ComboboxCollection>
												</ComboboxGroup>
											)}
										</ComboboxList>
									</ComboboxContent>
								</Combobox>
							</InputGroupAddon>
						</ComboboxInput>
						<ComboboxContent>
							<ComboboxStatus>{statusMessage}</ComboboxStatus>
							<ComboboxList>
								{isTeamRow
									? (config: Api.ITeamMeetingConfig) => {
											const memberCount = config.meetingConfigs?.length ?? 0;
											const disabled = !isTeamConfigOffered(config);
											return (
												<ComboboxItem key={config.id} value={config} disabled={disabled}>
													<ComboboxItemRadioIndicator />
													{config.name}
													<span className={css(styles.teamMeetingItemMeta)}>
														{memberCount} member{memberCount === 1 ? '' : 's'}
													</span>
													{disabled ? <span className={css(styles.disabledTag)}>Disabled</span> : null}
												</ComboboxItem>
											);
										}
									: (config: Api.IMeetingConfigResponse) => (
											<ComboboxItem key={config.id} value={config}>
												<ComboboxItemRadioIndicator />
												{config.published?.name ?? config.draft?.name}
											</ComboboxItem>
										)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
				{isMissingTeamConfig ? (
					<div className={css(styles.errorMessage)}>This team meeting link no longer exists.</div>
				) : null}
				{selectedTeamConfigIsNotOffered ? (
					<div className={css(styles.warningMessage)}>
						This team meeting link is disabled or has no enabled members, so the assistant will not offer it.
					</div>
				) : null}
				<TextArea
					styles={[styles.textArea]}
					value={resource.description ?? ''}
					onChange={e => onResourceChange(index, { ...resource, description: e.target.value })}
					placeholder='Describe when this calendar should be used'
					disabled={isSubmitting}
				/>
			</div>
			{canRemove ? (
				<Button
					type='button'
					variant='ghost'
					onClick={() => onRemove(index)}
					disabled={isSubmitting}
					aria-label='Remove calendar'
				>
					<TrashIcon fillColor={charmGray} />
				</Button>
			) : null}
		</div>
	);
}

interface CustomerAssistantEditorProps {
	accountId: string;
}

export function CustomerAssistantEditor({ accountId }: CustomerAssistantEditorProps) {
	const { data: account } = useAdminAccountSuspenseQuery({ accountId });
	const isMeetingSchedulerEnabled = !!account?.features?.meetingScheduler?.enabled;
	const accountImpersonationContext: Api.IImpersonationContext = React.useMemo(() => ({ account }), [account]);
	const { data: aiAgentTemplateData } = useAIAgentTemplateFilterInfiniteSuspenseQuery({
		filter: {
			criteria: [
				{ property: AIAgentTemplateFilterProperty.Scope, value: AIAgentTemplateScope.Account },
				{ property: AIAgentTemplateFilterProperty.ConversationType, value: AIConversationType.Custom },
				{ property: AIAgentTemplateFilterProperty.IsActive, value: 'true' },
			],
		},
		impersonationContext: accountImpersonationContext,
	});

	const existingTemplate = aiAgentTemplateData.pages[0]?.values?.[0];
	const isCreateMode = !existingTemplate;
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();

	// Load all users (under account context) so the admin selector can populate.
	const { users: allUsers, isLoading: isLoadingUsers } = useLoadAllUsers({
		impersonationContext: accountImpersonationContext,
		pageSize: 500,
	});
	const activeUsers = React.useMemo(() => allUsers.filter(user => user.activationStatus === 'ACTIVE'), [allUsers]);
	const activeAdmins = React.useMemo(
		() => activeUsers.filter(user => user.role === 'admin' || user.role === 'superAdmin'),
		[activeUsers]
	);
	const adminStatusMessage = React.useMemo(() => {
		if (isLoadingUsers) return 'Loading admins...';
		if (activeAdmins.length === 0) return 'No active admins available';
		return `${activeAdmins.length} admin${activeAdmins.length === 1 ? '' : 's'} available`;
	}, [activeAdmins.length, isLoadingUsers]);

	// Selected admin to act on behalf of. All creation/editing and chat go through this user.
	// Clearing the combobox re-triggers auto-selection.
	const [selectedAdminId, setSelectedAdminId] = React.useState<string | null>(null);
	React.useEffect(() => {
		if (selectedAdminId !== null || isLoadingUsers || activeAdmins.length === 0) {
			return;
		}
		const creatorId = existingTemplate?.creator?.id;
		if (creatorId && activeAdmins.some(u => u.id === creatorId)) {
			setSelectedAdminId(creatorId);
			return;
		}
		const mainContactId = account.additionalInfo?.mainContactUserId;
		if (mainContactId && activeAdmins.some(u => u.id === mainContactId)) {
			setSelectedAdminId(mainContactId);
			return;
		}
		setSelectedAdminId(activeAdmins[0]?.id ?? null);
	}, [
		activeAdmins,
		isLoadingUsers,
		account.additionalInfo?.mainContactUserId,
		existingTemplate?.creator?.id,
		selectedAdminId,
	]);

	const selectedAdmin = React.useMemo(
		() => (selectedAdminId ? (activeAdmins.find(u => u.id === selectedAdminId) ?? null) : null),
		[activeAdmins, selectedAdminId]
	);

	// Used only for the create/update mutations and the chat builder, so the backend sees
	// those actions as performed by the selected admin. All other reads stay account-scoped.
	const userImpersonationContext: Api.IImpersonationContext = React.useMemo(
		() => (selectedAdmin?.id ? { account, user: { id: selectedAdmin.id } } : accountImpersonationContext),
		[account, accountImpersonationContext, selectedAdmin?.id]
	);

	const { data, isLoading } = useInfiniteCustomSurveys({
		impersonationContext: accountImpersonationContext,
		pageSize: 500,
	});
	const surveys = data?.pages.flatMap(page => page.values) ?? [];

	const { data: surveyTemplateData, isLoading: isLoadingSurveyTemplates } = useBrowseAllSurveyTemplatesQuery({
		impersonationContext: accountImpersonationContext,
	});
	const surveyTemplates = surveyTemplateData ?? [];

	type SurveyOrTemplate = Api.ICustomSurvey | Api.ISurveyTemplateCard;
	type SurveyGroup = { value: string; items: SurveyOrTemplate[] };

	const surveyComboboxGroups: SurveyGroup[] = [
		{ value: 'User Surveys', items: surveys },
		{ value: 'System Survey Templates', items: surveyTemplates },
	];

	const surveysStatusMessage = React.useMemo(() => {
		if (isLoading || isLoadingSurveyTemplates) return 'Loading surveys...';
		const total = surveys.length + surveyTemplates.length;
		if (total === 0) return 'No surveys found';
		return `${total} survey${total === 1 ? '' : 's'} available`;
	}, [isLoading, isLoadingSurveyTemplates, surveys.length, surveyTemplates.length]);

	// Event Registration Surveys query
	const { data: eventData, isLoading: isLoadingEvents } = useInfiniteEventRegistrationSurveys({
		impersonationContext: accountImpersonationContext,
		pageSize: 500,
	});
	const eventSurveys = eventData?.pages.flatMap(page => page.values) ?? [];
	const eventSurveysStatusMessage = React.useMemo(() => {
		if (isLoadingEvents) return 'Loading event surveys...';
		if (eventSurveys.length === 0) return 'No event surveys found';
		return `${eventSurveys.length} event survey${eventSurveys.length === 1 ? '' : 's'} available`;
	}, [isLoadingEvents, eventSurveys.length]);

	// Team meeting links (round-robin) offered alongside user calendars in the meeting picker
	const { data: teamMeetingConfigsData, isLoading: isLoadingTeamConfigs } = useTeamMeetingConfigsQuery({
		pageSize: 100,
		impersonationContext: accountImpersonationContext,
		enabled: isMeetingSchedulerEnabled,
	});
	const teamMeetingConfigs = teamMeetingConfigsData?.values ?? [];

	// Editor mode tabs
	const [editorMode, setEditorMode] = React.useState<'standalone' | 'embedded-widget'>('standalone');
	const [embedPosition, setEmbedPosition] = React.useState<AssistantEmbedPosition>('bottom-right');
	const [embedLauncherLabel, setEmbedLauncherLabel] = React.useState('Chat with our AI assistant');
	const [embedPrimaryColor, setEmbedPrimaryColor] = React.useState(brandPrimary);
	const [embedForegroundColorAuto, setEmbedForegroundColorAuto] = React.useState(true);
	const [embedForegroundColor, setEmbedForegroundColor] = React.useState('#ffffff');
	const effectiveForegroundColor = embedForegroundColorAuto
		? getContrastTextColor(embedPrimaryColor || brandPrimary)
		: embedForegroundColor;

	const subverticals = account.preferences?.subverticalSettings?.subverticals;

	const [formData, setFormData] = React.useState<AIAgentTemplateFormData>(() =>
		existingTemplate ? getInitialFormDataFromTemplate(existingTemplate) : getEmptyFormData(subverticals)
	);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [formErrors, setFormErrors] = React.useState<Record<string, string>>({});
	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

	// Reset form when template changes (e.g., after create succeeds, switches to edit mode, or after deletion)
	React.useEffect(() => {
		if (existingTemplate) {
			setFormData(getInitialFormDataFromTemplate(existingTemplate));
		} else {
			setFormData(getEmptyFormData(subverticals));
			setFormErrors({});
		}
	}, [existingTemplate, subverticals]);

	// Derive avatar preview URL from form data
	const avatarPreviewUrl = formData.agentAvatarImage?.url ?? null;

	// Derive conversation starter strings for UI display
	const conversationStarterStrings = React.useMemo(() => {
		const starters = formData.conversationStarters ?? [];
		if (starters.length === 0) return [''];
		return starters.map(s => s.title);
	}, [formData.conversationStarters]);

	// Image attachment mutation for avatar upload
	const imageAttachmentMutation = useImageAttachmentMutation({
		onSuccess: attachments => {
			if (attachments.length > 0) {
				setFormData(prev => ({ ...prev, agentAvatarImage: attachments[0] }));
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Handle avatar image changes - upload immediately when file is selected
	const handleAvatarChange = React.useCallback(
		(file: File | null) => {
			if (file) {
				imageAttachmentMutation.mutate({
					files: [file],
					impersonationContext: accountImpersonationContext,
				});
			} else {
				// Soft delete - just remove reference from form data
				setFormData(prev => ({ ...prev, agentAvatarImage: null }));
			}
		},
		[imageAttachmentMutation, accountImpersonationContext]
	);

	// Mutation hooks for API calls
	const updateMutation = useUpdateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [AI_AGENT_TEMPLATES_QUERY_KEY] });
			toast.success('Customer Assistant updated successfully');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSettled: () => {
			invalidateAIAgentTemplateFilter();
		},
	});

	const createMutation = useCreateAIAgentTemplateMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [AI_AGENT_TEMPLATES_QUERY_KEY] });
			toast.success('Customer Assistant created successfully');
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSettled: () => {
			invalidateAIAgentTemplateFilter();
		},
	});

	const deleteMutation = useDeleteAIAgentTemplateMutation({
		// Invalidate only on success — no need to refetch if delete failed (nothing changed)
		onSuccess: () => {
			invalidateAIAgentTemplateFilter();
			toast.success('Customer assistant deleted successfully');
			setShowDeleteConfirmation(false);
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Simplified validation function (removed conversationType and technicalPrompt)
	const validateForm = () => {
		const errors: Record<string, string> = {};

		if (!formData.name.trim()) {
			errors.name = 'Name is required';
		} else if (formData.name.length > 255) {
			errors.name = 'Name must be less than 255 characters';
		}

		if (formData.description && formData.description.length > 1000) {
			errors.description = 'Description must be less than 1000 characters';
		}

		if (!formData.objectivePrompt.trim()) {
			errors.objectivePrompt = 'Instructions are required';
		} else if (formData.objectivePrompt.length > MAX_OBJECTIVE_PROMPT_LENGTH) {
			errors.objectivePrompt = 'Instructions must be 60,000 characters or fewer.';
		}

		// Validate allowed origins when public access is enabled
		if (formData.anonymousAccess?.enabled) {
			const origins = formData.anonymousAccess.allowedOrigins ?? [];
			const normalizedOrigins = origins.map(normalizeHttpUrl).filter(origin => origin !== '');
			const invalidOrigins = normalizedOrigins.filter(origin => !isValidEmbeddedAllowedOrigin(origin));
			if (invalidOrigins.length > 0) {
				errors.allowedOrigins =
					'Each whitelisted URL must be a valid https origin (e.g. https://example.com) with no path, query, or hash';
			}
		}

		const webSearchCapability = (formData.capabilities ?? []).find(c => c._type === 'WebSearchCapability') as
			| WebSearchCapability
			| undefined;
		const normalizedWebSearchDomains = (webSearchCapability?.allowedDomains ?? [])
			.map(normalizeHttpUrl)
			.filter(domain => domain !== '');
		const invalidWebSearchDomains = normalizedWebSearchDomains.filter(domain => !isValidWebSearchDomain(domain));
		if (invalidWebSearchDomains.length > 0) {
			errors.webSearchDomains = 'Each Web Search URL must be valid and start with http:// or https://';
		}

		return errors;
	};

	// Form submission handler
	const onSubmit = async () => {
		setIsSubmitting(true);
		setFormErrors({});
		// Validate form
		const errors = validateForm();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
			setIsSubmitting(false);
			return;
		}

		// Clean form data before submission
		const cleanedFormData: Api.IAIAgentTemplateFormData = {
			...formData,
			scope: Api.AIAgentTemplateScope.Account,
			category: Api.AIConversationCategory.Public,
		};

		// Clean anonymousAccess allowedOrigins - filter empty strings
		if (cleanedFormData.anonymousAccess) {
			const filteredOrigins = (cleanedFormData.anonymousAccess.allowedOrigins ?? [])
				.map(normalizeHttpUrl)
				.filter(origin => origin !== '');
			cleanedFormData.anonymousAccess = {
				...cleanedFormData.anonymousAccess,
				allowedOrigins: filteredOrigins,
			};
		}

		// Clean capabilities
		if (cleanedFormData.capabilities) {
			// Step 1: Filter empty entries within each capability
			const cleanedCapabilities = cleanedFormData.capabilities
				.map(capability => {
					if (capability._type === 'WebSearchCapability') {
						const webCap = capability as WebSearchCapability;
						const filteredDomains = (webCap.allowedDomains ?? []).map(normalizeHttpUrl).filter(domain => domain !== '');
						return {
							...webCap,
							allowedDomains: filteredDomains,
						};
					} else if (
						capability._type === 'CustomSurveyCapability' ||
						capability._type === 'EventRegistrationCapability' ||
						capability._type === 'MeetingCapability'
					) {
						const resourceCap = capability as CustomSurveyCapability | EventRegistrationCapability | MeetingCapability;
						const filteredResources = (resourceCap.resources ?? []).filter(
							resource => resource.resourceId.trim() !== '' || !!resource.templateId?.trim()
						);
						return {
							...resourceCap,
							resources: filteredResources,
						};
					}
					return capability;
				})
				// Step 2: Filter out capabilities with empty arrays
				.filter(capability => {
					if (capability._type === 'WebSearchCapability') {
						const webCap = capability as WebSearchCapability;
						return (webCap.allowedDomains ?? []).length > 0;
					} else if (capability._type === 'EventRegistrationCapability') {
						const eventCap = capability as EventRegistrationCapability;
						// Keep if includeAllUpcomingEvents is true OR has resources
						return eventCap.includeAllUpcomingEvents || (eventCap.resources ?? []).length > 0;
					} else if (capability._type === 'CustomSurveyCapability' || capability._type === 'MeetingCapability') {
						const resourceCap = capability as CustomSurveyCapability | MeetingCapability;
						return (resourceCap.resources ?? []).length > 0;
					}
					return true; // Keep other capability types
				});

			cleanedFormData.capabilities = cleanedCapabilities;
		}

		const hasTemplateResources = (cleanedFormData.capabilities ?? []).some(
			capability =>
				capability._type === 'CustomSurveyCapability' &&
				((capability as CustomSurveyCapability).resources ?? []).some(r => !!r.templateId?.trim())
		);

		try {
			if (isCreateMode) {
				await createMutation.mutateAsync({
					data: cleanedFormData,
					impersonationContext: userImpersonationContext,
				});
			} else {
				// For updates, spread the original template data to preserve fields not in the form
				const updateData: AIAgentTemplateFormData = {
					...existingTemplate,
					...cleanedFormData,
				};
				await updateMutation.mutateAsync({
					id: existingTemplate!.id,
					data: updateData,
					impersonationContext: userImpersonationContext,
				});
			}

			if (hasTemplateResources) {
				invalidateInfiniteCustomSurveys();
			}
		} catch (error) {
			errorMessages.pushApiError(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleFormSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		onSubmit();
	};

	const handleDeleteConfirm = () => {
		if (!existingTemplate) return;
		deleteMutation.mutate({ id: existingTemplate.id, impersonationContext: accountImpersonationContext });
	};

	const handleAttachmentsChange = (attachments: IAIAgentTemplateAttachment[]) => {
		setFormData(prev => ({ ...prev, attachments }));
	};

	const clearFormError = (field: string) => {
		setFormErrors(prev => {
			if (!prev[field]) {
				return prev;
			}

			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	// Conversation starters handlers
	const handleConversationStarterChange = (index: number, value: string) => {
		setFormData(prev => {
			const currentStarters = prev.conversationStarters ?? [];
			const updated = [...currentStarters];
			updated[index] = stringToConversationStarter(value);
			return { ...prev, conversationStarters: updated };
		});
	};

	const handleAddConversationStarter = () => {
		if (conversationStarterStrings.some(starter => starter.trim() === '')) {
			return;
		}
		setFormData(prev => ({
			...prev,
			conversationStarters: [...(prev.conversationStarters ?? []), stringToConversationStarter('')],
		}));
	};

	const handleRemoveConversationStarter = (index: number) => {
		setFormData(prev => ({
			...prev,
			conversationStarters: (prev.conversationStarters ?? []).filter((_, i) => i !== index),
		}));
	};

	// Anonymous access (Public Access) handlers
	const handlePublicAccessToggle = (enabled: boolean) => {
		clearFormError('allowedOrigins');
		setFormData(prev => ({
			...prev,
			anonymousAccess: {
				...prev.anonymousAccess,
				enabled,
				allowedOrigins: prev.anonymousAccess?.allowedOrigins ?? [],
			},
		}));
	};

	const handleAllowedOriginChange = (index: number, value: string) => {
		clearFormError('allowedOrigins');

		// If the list was empty we're using a virtual placeholder row;
		// ensure the origin actually gets persisted into state.
		if ((formData.anonymousAccess?.allowedOrigins?.length ?? 0) === 0) {
			setFormData(prev => ({
				...prev,
				anonymousAccess: {
					...prev.anonymousAccess,
					enabled: prev.anonymousAccess?.enabled ?? false,
					allowedOrigins: [value],
				},
			}));
		} else {
			setFormData(prev => {
				const currentOrigins = prev.anonymousAccess?.allowedOrigins ?? [];
				const updated = [...currentOrigins];
				updated[index] = value;
				return {
					...prev,
					anonymousAccess: {
						...prev.anonymousAccess,
						enabled: prev.anonymousAccess?.enabled ?? false,
						allowedOrigins: updated,
					},
				};
			});
		}
	};

	const handleAddAllowedOrigin = () => {
		setFormData(prev => ({
			...prev,
			anonymousAccess: {
				...prev.anonymousAccess,
				enabled: prev.anonymousAccess?.enabled ?? false,
				allowedOrigins: [...(prev.anonymousAccess?.allowedOrigins ?? []), ''],
			},
		}));
	};

	const handleRemoveAllowedOrigin = (index: number) => {
		clearFormError('allowedOrigins');
		setFormData(prev => ({
			...prev,
			anonymousAccess: {
				...prev.anonymousAccess,
				enabled: prev.anonymousAccess?.enabled ?? false,
				allowedOrigins: (prev.anonymousAccess?.allowedOrigins ?? []).filter((_, i) => i !== index),
			},
		}));
	};
	let surveyResources: CapabilityResourceConfiguration[] = [];
	if (formData.capabilities) {
		const surveyCapability = formData.capabilities.find(c => c._type === 'CustomSurveyCapability') as
			| CustomSurveyCapability
			| undefined;
		if (surveyCapability) {
			surveyResources = surveyCapability.resources ?? [];
		}
	}

	// Event Registration Survey resources
	let eventResources: CapabilityResourceConfiguration[] = [];
	let includeAllUpcomingEvents = true;
	if (formData.capabilities) {
		const eventCapability = formData.capabilities.find(c => c._type === 'EventRegistrationCapability') as
			| EventRegistrationCapability
			| undefined;
		if (eventCapability) {
			eventResources = eventCapability.resources ?? [];
			includeAllUpcomingEvents = eventCapability.includeAllUpcomingEvents ?? true;
		}
	}

	// Meeting resources
	let meetingResources: CapabilityResourceConfiguration[] = [];
	if (formData.capabilities) {
		const meetingCapability = formData.capabilities.find(c => c._type === 'MeetingCapability') as
			| MeetingCapability
			| undefined;
		if (meetingCapability) {
			meetingResources = meetingCapability.resources ?? [];
		}
	}

	// Web Search allowed domains
	let webSearchDomains: string[] = [];
	if (formData.capabilities) {
		const webSearchCapability = formData.capabilities.find(c => c._type === 'WebSearchCapability') as
			| WebSearchCapability
			| undefined;
		if (webSearchCapability) {
			webSearchDomains = webSearchCapability.allowedDomains ?? [''];
		}
	}
	if (webSearchDomains.length === 0) {
		webSearchDomains = [''];
	}

	const setSurveyResources = (resources: CapabilityResourceConfiguration[]) => {
		setFormData(prev => {
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'CustomSurveyCapability'),
					{
						_type: 'CustomSurveyCapability',
						resources,
					} as CustomSurveyCapability,
				],
			};
		});
	};

	const setEventResources = (resources: CapabilityResourceConfiguration[]) => {
		setFormData(prev => {
			const existingCapability = prev.capabilities?.find(c => c._type === 'EventRegistrationCapability') as
				| EventRegistrationCapability
				| undefined;
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'EventRegistrationCapability'),
					{
						_type: 'EventRegistrationCapability',
						includeAllUpcomingEvents: existingCapability?.includeAllUpcomingEvents ?? true,
						resources,
					} as EventRegistrationCapability,
				],
			};
		});
	};

	const setMeetingResources = (resources: CapabilityResourceConfiguration[]) => {
		setFormData(prev => {
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'MeetingCapability'),
					{
						_type: 'MeetingCapability',
						resources,
					} as MeetingCapability,
				],
			};
		});
	};

	const setWebSearchDomains = (allowedDomains: string[]) => {
		setFormData(prev => {
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'WebSearchCapability'),
					{
						_type: 'WebSearchCapability',
						allowedDomains,
					} as WebSearchCapability,
				],
			};
		});
	};

	const handleAddSurvey = () => {
		setSurveyResources([...surveyResources, { resourceId: '', description: '' }]);
	};
	const handleSurveyChange = (index: number, item: Api.ICustomSurvey | Api.ISurveyTemplateCard | null) => {
		const updated = [...surveyResources];
		if (!item) {
			updated[index] = { ...updated[index], resourceId: '', templateId: undefined };
		} else if (item._type === 'CustomSurvey') {
			updated[index] = { ...updated[index], resourceId: item.id, templateId: undefined };
		} else {
			updated[index] = { ...updated[index], resourceId: '', templateId: item.id };
		}
		setSurveyResources(updated);
	};
	const handleDescriptionChange = (index: number, description: string) => {
		const updated = [...surveyResources];
		updated[index] = {
			...updated[index],
			description,
		};
		setSurveyResources(updated);
	};
	const handleRemoveSurvey = (index: number) => {
		setSurveyResources(surveyResources.filter((_, i) => i !== index));
	};

	// Event Survey handlers
	const handleAddEventSurvey = () => {
		setEventResources([...eventResources, { resourceId: '', description: '' }]);
	};
	const handleEventSurveyChange = (index: number, survey: Api.IEventRegistrationSurvey | null) => {
		const updated = [...eventResources];
		updated[index] = {
			...updated[index],
			resourceId: survey?.id ?? '',
		};
		setEventResources(updated);
	};
	const handleRemoveEventSurvey = (index: number) => {
		setEventResources(eventResources.filter((_, i) => i !== index));
	};

	const handleIncludeAllUpcomingEventsToggle = (enabled: boolean) => {
		setFormData(prev => {
			const existingCapability = prev.capabilities?.find(c => c._type === 'EventRegistrationCapability') as
				| EventRegistrationCapability
				| undefined;
			return {
				...prev,
				capabilities: [
					...(prev.capabilities ?? []).filter(c => c._type !== 'EventRegistrationCapability'),
					{
						_type: 'EventRegistrationCapability',
						includeAllUpcomingEvents: enabled,
						resources: existingCapability?.resources ?? [{ resourceId: '' }],
					} as EventRegistrationCapability,
				],
			};
		});
	};

	// Meeting config handlers
	const handleAddMeeting = () => {
		setMeetingResources([...meetingResources, { resourceId: '', description: '' }]);
	};
	const handleMeetingResourceChange = (index: number, resource: CapabilityResourceConfiguration) => {
		const updated = [...meetingResources];
		updated[index] = resource;
		setMeetingResources(updated);
	};
	const handleRemoveMeeting = (index: number) => {
		setMeetingResources(meetingResources.filter((_, i) => i !== index));
	};

	// Web Search handlers
	const handleAddWebsite = () => {
		setWebSearchDomains([...webSearchDomains, '']);
	};
	const handleWebsiteChange = (index: number, domain: string) => {
		clearFormError('webSearchDomains');
		const updated = [...webSearchDomains];
		updated[index] = domain;
		setWebSearchDomains(updated);
	};
	const handleRemoveWebsite = (index: number) => {
		clearFormError('webSearchDomains');
		setWebSearchDomains(webSearchDomains.filter((_, i) => i !== index));
	};

	const embedSnippet = existingTemplate?.id
		? getAssistantEmbedSnippet({
				agentTemplateId: existingTemplate.id,
				position: embedPosition,
				launcherLabel: embedLauncherLabel,
				primaryColor: embedPrimaryColor,
				foregroundColor: embedForegroundColorAuto ? undefined : embedForegroundColor,
			})
		: '';

	const handleCopyEmbedSnippet = async () => {
		if (!embedSnippet) {
			return;
		}
		const done = await copyToClipboard(embedSnippet);
		if (done) {
			toast.success('Embed snippet copied to clipboard.');
			return;
		}
		toast.error('Unable to copy to clipboard.');
	};

	return (
		<TabRoot defaultValue='form' className={css(styles.outerContainer)}>
			<div className={css(styles.stickyTopBar)}>
				<TabList styles={[styles.stickyTopBarTabs]}>
					<TabTrigger value='form'>Form</TabTrigger>
					<TabTrigger value='chat-builder'>Chat Builder</TabTrigger>
				</TabList>
				<div className={css(styles.stickyTopBarActions)}>
					<Field orientation='horizontal'>
						<FieldLabel>Acting on behalf of</FieldLabel>
						<Combobox
							items={activeAdmins}
							value={selectedAdmin}
							onValueChange={value => setSelectedAdminId((value as Api.IUser | null)?.id ?? null)}
							itemToStringLabel={item => getDisplayName(item as Api.IUser) ?? (item as Api.IUser)?.email ?? ''}
							isItemEqualToValue={(a, b) => (a as Api.IUser)?.id === (b as Api.IUser)?.id}
						>
							<ComboboxInput
								id='customer-assistant-impersonation-combobox'
								placeholder={
									isLoadingUsers
										? 'Loading admins...'
										: activeAdmins.length === 0
											? 'No active admins available'
											: 'Search admins...'
								}
								disabled={isLoadingUsers || activeAdmins.length === 0}
								styles={[styles.behalfCombo]}
							/>
							<ComboboxContent>
								<ComboboxStatus>{adminStatusMessage}</ComboboxStatus>
								<ComboboxList>{(user: Api.IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</Field>
					{!isCreateMode && existingTemplate?.id && (
						<Button
							type='button'
							size='sm'
							variant='secondary'
							disabled={!existingTemplate?.anonymousAccess?.enabled}
							onClick={() =>
								window.open(
									getAssistantStarterUrl({ agentTemplateId: existingTemplate.id }),
									'_blank',
									'noopener,noreferrer'
								)
							}
							title={!existingTemplate?.anonymousAccess?.enabled ? 'Enable public access to view' : undefined}
						>
							<EyeIcon fillColor='currentColor' />
							Preview
						</Button>
					)}
					{!isCreateMode && existingTemplate?.id && (
						<Popover>
							<PopoverTrigger asChild>
								<Button
									size='sm'
									type='button'
									variant='secondary'
									disabled={!existingTemplate?.anonymousAccess?.enabled}
									title={
										!existingTemplate?.anonymousAccess?.enabled
											? 'Enable public access to copy embed snippet'
											: undefined
									}
								>
									<CodeIcon />
									Embed
								</Button>
							</PopoverTrigger>
							<PopoverContent align='end' side='bottom' styles={[styles.embedPopover]}>
								<div className={css(styles.embedPopoverLayout)}>
									<div className={css(styles.embedOptionsContainer)}>
										<div className={css(styles.embedOptionGroup)}>
											<label className={css(styles.embedOptionLabel)}>Launcher position</label>
											<Select
												items={EMBED_POSITION_OPTIONS}
												value={embedPosition}
												onValueChange={value => setEmbedPosition(value as AssistantEmbedPosition)}
											>
												<SelectTrigger styles={[bs.wFull]}>
													<SelectValue placeholder='Select launcher position' />
												</SelectTrigger>
												<SelectContent>
													{EMBED_POSITION_OPTIONS.map(option => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div className={css(styles.embedOptionGroup)}>
											<label className={css(styles.embedOptionLabel)}>Launcher label</label>
											<Input
												type='text'
												value={embedLauncherLabel}
												onChange={event => setEmbedLauncherLabel(event.target.value)}
												disabled={isSubmitting}
											/>
										</div>
										<div className={css(styles.embedOptionGroup)}>
											<label className={css(styles.embedOptionLabel)}>Primary color</label>
											<ColorPicker value={embedPrimaryColor} onChange={setEmbedPrimaryColor} />
										</div>
										<div className={css(styles.embedOptionGroup)}>
											<div className={css(styles.publicAccessToggleRow)}>
												<label className={css(styles.embedOptionLabel)}>Foreground color</label>
												<Toggle
													id='embed-foreground-color-auto-toggle'
													isOn={embedForegroundColorAuto}
													onToggleCheckChanged={setEmbedForegroundColorAuto}
													text='Auto'
												/>
											</div>
											{!embedForegroundColorAuto ? (
												<ColorPicker value={embedForegroundColor} onChange={setEmbedForegroundColor} />
											) : null}
										</div>
									</div>
									<div className={css(styles.embedOptionGroup)}>
										<label className={css(styles.embedOptionLabel)}>Embed script</label>
										<InputGroup>
											<InputGroupTextarea
												value={embedSnippet}
												readOnly
												onFocus={event => event.target.select()}
												styles={[styles.embedSnippetInput]}
											/>
										</InputGroup>
									</div>
									<Button
										type='button'
										variant='primary'
										size='sm'
										onClick={handleCopyEmbedSnippet}
										aria-label='Copy embed snippet'
										title='Copy embed snippet'
										styles={[styles.embedCopyButton]}
									>
										<CopyIcon fillColor='currentColor' /> Copy embed script
									</Button>
								</div>
							</PopoverContent>
						</Popover>
					)}
					{!isCreateMode && existingTemplate?.id && (
						<Button
							size='sm'
							type='button'
							variant='destructive-secondary'
							disabled={isSubmitting || deleteMutation.isPending}
							onClick={() => setShowDeleteConfirmation(true)}
						>
							Delete
						</Button>
					)}
					<Button
						size='sm'
						type='submit'
						form='customer-assistant-form'
						disabled={isSubmitting || updateMutation.isPending || createMutation.isPending}
					>
						Save
					</Button>
				</div>
			</div>
			<div className={css(styles.twoColumnContainer)}>
				<div className={css(styles.leftTabRoot)}>
					<TabContent value='form' className={css(styles.leftTabContent)}>
						<form id='customer-assistant-form' onSubmit={handleFormSubmit} className={css(styles.editorPanel)}>
							{/* Avatar Image Uploader */}
							<div className={css(styles.avatarUploaderContainer)}>
								<AvatarImageUploader
									previewUrl={avatarPreviewUrl}
									onImageChange={handleAvatarChange}
									disabled={isSubmitting}
									isUploading={imageAttachmentMutation.isPending}
								/>
								<div className={css(styles.assistantImageLabel)}>Assistant image</div>
							</div>

							{/* Name */}
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Name</label>
								<Input
									type='text'
									value={formData.name}
									onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
									placeholder='Enter assistant name'
									disabled={isSubmitting}
								/>
								{formErrors.name && <div className={css(styles.errorMessage)}>{formErrors.name}</div>}
							</div>

							{/* Description */}
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Description</label>
								<TextArea
									styles={[styles.textArea]}
									value={formData.description}
									onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
									placeholder='Enter assistant description'
									disabled={isSubmitting}
								/>
								{formErrors.description && <div className={css(styles.errorMessage)}>{formErrors.description}</div>}
							</div>

							{/* Instructions (renamed from Objective Prompt) */}
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Instructions</label>
								<TextArea
									styles={[styles.textArea, styles.instructionsArea]}
									value={formData.objectivePrompt}
									onChange={e => setFormData(prev => ({ ...prev, objectivePrompt: e.target.value }))}
									placeholder='Enter instructions for the assistant'
									disabled={isSubmitting}
								/>
								{formErrors.objectivePrompt && (
									<div className={css(styles.errorMessage)}>{formErrors.objectivePrompt}</div>
								)}
							</div>

							{/* Conversation Starters */}
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Conversation Starters</label>
								<div className={css(styles.conversationStartersContainer)}>
									{conversationStarterStrings.map((starter, index) => (
										<div key={index} className={css(styles.conversationStarterRow)}>
											<Input
												type='text'
												value={starter}
												onChange={e => handleConversationStarterChange(index, e.target.value)}
												disabled={isSubmitting}
												styles={[styles.conversationStarterInput]}
											/>
											{conversationStarterStrings.length > 1 && (
												<Button
													type='button'
													variant='ghost'
													onClick={() => handleRemoveConversationStarter(index)}
													disabled={isSubmitting}
													aria-label='Remove conversation starter'
												>
													<TrashIcon fillColor={charmGray} />
												</Button>
											)}
										</div>
									))}
									<Button
										type='button'
										variant='link'
										onClick={handleAddConversationStarter}
										disabled={isSubmitting}
										styles={[styles.addConversationStarterLink]}
									>
										+ Add Conversation Starter
									</Button>
								</div>
							</div>

							{/* Public Access Toggle */}
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Public Access</label>
								<div className={css(styles.publicAccessToggleRow)}>
									<Toggle
										id='public-access-toggle'
										isOn={formData.anonymousAccess?.enabled ?? false}
										onToggleCheckChanged={handlePublicAccessToggle}
										disabled={isSubmitting}
									/>
									<span className={css(styles.publicAccessLabel)}>Enabled</span>
								</div>
							</div>
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Whitelisted host website URLs (For embedded mode)</label>
								<div className={css(styles.allowedOriginsContainer)}>
									{(formData.anonymousAccess?.allowedOrigins?.length
										? formData.anonymousAccess.allowedOrigins
										: ['']
									).map((origin, index) => (
										<div key={index} className={css(styles.allowedOriginRow)}>
											<Input
												type='text'
												value={origin}
												onChange={e => {
													handleAllowedOriginChange(index, e.target.value);
												}}
												placeholder='https://example.com'
												disabled={isSubmitting}
												styles={[styles.allowedOriginInput]}
											/>
											{(formData.anonymousAccess?.allowedOrigins?.length ?? 0) > 1 && (
												<Button
													type='button'
													variant='ghost'
													onClick={() => handleRemoveAllowedOrigin(index)}
													disabled={isSubmitting}
													aria-label='Remove whitelisted URL'
												>
													<TrashIcon fillColor={charmGray} />
												</Button>
											)}
										</div>
									))}
									<Button
										type='button'
										variant='link'
										onClick={handleAddAllowedOrigin}
										disabled={isSubmitting}
										styles={[styles.addAllowedOriginLink]}
									>
										+ Add URL
									</Button>
									{formErrors.allowedOrigins && (
										<div className={css(styles.errorMessage)}>{formErrors.allowedOrigins}</div>
									)}
								</div>
							</div>
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Surveys</label>
								<div className={css(styles.surveyCapabilityContainer)}>
									{surveyResources.map((resource, index) => {
										const selectedItem: SurveyOrTemplate | null = resource.resourceId
											? (surveyComboboxGroups[0].items.find(i => i.id === resource.resourceId) ?? null)
											: resource.templateId
												? (surveyComboboxGroups[1].items.find(i => i.id === resource.templateId) ?? null)
												: null;

										return (
											<div key={index} className={css(styles.surveyRow)}>
												<div className={css(styles.surveyRowHeader)}>
													<div className={css(styles.surveyCombobox)}>
														<Combobox
															items={surveyComboboxGroups}
															value={selectedItem}
															onValueChange={value => handleSurveyChange(index, value as SurveyOrTemplate | null)}
															itemToStringLabel={item => (item as SurveyOrTemplate)?.name ?? ''}
															isItemEqualToValue={(a, b) =>
																(a as SurveyOrTemplate)?._type === (b as SurveyOrTemplate)?._type &&
																(a as SurveyOrTemplate)?.id === (b as SurveyOrTemplate)?.id
															}
														>
															<ComboboxInput placeholder='Search surveys...' disabled={isSubmitting} showClear />
															<ComboboxContent>
																<ComboboxStatus>{surveysStatusMessage}</ComboboxStatus>
																<ComboboxList styles={[bs.pt0]}>
																	{(group: SurveyGroup) => (
																		<ComboboxGroup key={group.value} items={group.items}>
																			<ComboboxGroupLabel>{group.value}</ComboboxGroupLabel>
																			<ComboboxCollection>
																				{(item: SurveyOrTemplate) => (
																					<ComboboxItem key={`${item._type}-${item.id}`} value={item}>
																						<ComboboxItemRadioIndicator />
																						{item._type === 'CustomSurvey' ? (
																							<NotepadTextIcon className={css(styles.surveyItemIcon)} />
																						) : (
																							<NotepadTextDashedIcon className={css(styles.surveyItemIcon)} />
																						)}
																						{item.name}
																					</ComboboxItem>
																				)}
																			</ComboboxCollection>
																		</ComboboxGroup>
																	)}
																</ComboboxList>
															</ComboboxContent>
														</Combobox>
													</div>
													<TextArea
														styles={[styles.textArea]}
														value={resource.description ?? ''}
														onChange={e => handleDescriptionChange(index, e.target.value)}
														placeholder='Describe when this survey should be used'
														disabled={isSubmitting}
													/>
												</div>
												{surveyResources.length > 1 ? (
													<Button
														type='button'
														variant='ghost'
														onClick={() => handleRemoveSurvey(index)}
														disabled={isSubmitting}
														aria-label='Remove survey'
													>
														<TrashIcon fillColor={charmGray} />
													</Button>
												) : null}
											</div>
										);
									})}
									<Button
										type='button'
										variant='link'
										onClick={handleAddSurvey}
										disabled={isSubmitting}
										styles={[styles.addSurveyLink]}
									>
										+ Add Survey
									</Button>
								</div>
							</div>

							{/* Event Surveys */}
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Event Surveys</label>
								<div className={css(styles.surveyCapabilityContainer)}>
									{/* Toggle for Include All Event Surveys */}
									<div className={css(styles.publicAccessToggleRow)}>
										<Toggle
											id='include-all-upcoming-events-toggle'
											isOn={includeAllUpcomingEvents}
											onToggleCheckChanged={handleIncludeAllUpcomingEventsToggle}
											disabled={isSubmitting}
										/>
										<span className={css(styles.publicAccessLabel)}>Include all event surveys</span>
									</div>
									<p className={css(styles.helpText)}>
										When enabled, the assistant will automatically have access to all non-archived future events.
									</p>

									{/* Only show individual event survey inputs when toggle is OFF */}
									{!includeAllUpcomingEvents && (
										<>
											{eventResources.map((resource, index) => {
												const selectedEventSurvey = resource.resourceId
													? (eventSurveys.find(s => s.id === resource.resourceId) ?? null)
													: null;

												return (
													<div key={index} className={css(styles.surveyRow)}>
														<div className={css(styles.surveyRowHeader)}>
															<div className={css(styles.surveyCombobox)}>
																<Combobox
																	items={eventSurveys}
																	value={selectedEventSurvey}
																	onValueChange={value =>
																		handleEventSurveyChange(index, value as Api.IEventRegistrationSurvey | null)
																	}
																	itemToStringLabel={survey => (survey as Api.IEventRegistrationSurvey)?.name ?? ''}
																	isItemEqualToValue={(item, value) =>
																		(item as Api.IEventRegistrationSurvey)?.id ===
																		(value as Api.IEventRegistrationSurvey)?.id
																	}
																>
																	<ComboboxInput
																		placeholder='Search event surveys...'
																		disabled={isSubmitting}
																		showClear
																	/>
																	<ComboboxContent>
																		<ComboboxStatus>{eventSurveysStatusMessage}</ComboboxStatus>
																		<ComboboxList>
																			{(survey: Api.IEventRegistrationSurvey) => (
																				<ComboboxItem key={survey.id} value={survey}>
																					<ComboboxItemRadioIndicator />
																					{survey.name}
																				</ComboboxItem>
																			)}
																		</ComboboxList>
																	</ComboboxContent>
																</Combobox>
															</div>
														</div>
														{eventResources.length > 1 ? (
															<Button
																type='button'
																variant='ghost'
																onClick={() => handleRemoveEventSurvey(index)}
																disabled={isSubmitting}
																aria-label='Remove event survey'
															>
																<TrashIcon fillColor={charmGray} />
															</Button>
														) : null}
													</div>
												);
											})}
											<Button
												type='button'
												variant='link'
												onClick={handleAddEventSurvey}
												disabled={isSubmitting}
												styles={[styles.addSurveyLink]}
											>
												+ Add Event Survey
											</Button>
										</>
									)}
								</div>
							</div>

							{/* Meeting Configs */}
							{isMeetingSchedulerEnabled && (
								<div className={css(styles.formGroup)}>
									<label className={css(styles.label)}>Calendars for meeting booking</label>
									<div className={css(styles.surveyCapabilityContainer)}>
										{meetingResources.map((resource, index) => (
											<MeetingResourceRow
												key={index}
												index={index}
												resource={resource}
												allUsers={activeUsers}
												isLoadingUsers={isLoadingUsers}
												isSubmitting={isSubmitting}
												canRemove={meetingResources.length > 1}
												impersonationContext={accountImpersonationContext}
												teamConfigs={teamMeetingConfigs}
												isLoadingTeamConfigs={isLoadingTeamConfigs}
												otherSelectedResourceIds={meetingResources
													.filter((_, i) => i !== index)
													.map(r => r.resourceId)
													.filter(Boolean)}
												onResourceChange={handleMeetingResourceChange}
												onRemove={handleRemoveMeeting}
											/>
										))}
										<Button
											type='button'
											variant='link'
											onClick={handleAddMeeting}
											disabled={isSubmitting}
											styles={[styles.addSurveyLink]}
										>
											+ Add Calendar
										</Button>
									</div>
								</div>
							)}

							{/* Web Search */}
							<div className={css(styles.formGroup)}>
								<label className={css(styles.label)}>Web Search</label>
								<div className={css(styles.surveyCapabilityContainer)}>
									{webSearchDomains.map((domain, index) => (
										<div key={index} className={css(styles.allowedOriginRow)}>
											<Input
												type='text'
												value={domain}
												onChange={e => handleWebsiteChange(index, e.target.value)}
												placeholder='Add a link to the AI knowledge base, such as https://www.clientcorp.com'
												disabled={isSubmitting}
												styles={[styles.allowedOriginInput]}
											/>
											{webSearchDomains.length > 1 && (
												<Button
													type='button'
													variant='ghost'
													onClick={() => handleRemoveWebsite(index)}
													disabled={isSubmitting}
													aria-label='Remove website'
												>
													<TrashIcon fillColor={charmGray} />
												</Button>
											)}
										</div>
									))}
									<Button
										type='button'
										variant='link'
										onClick={handleAddWebsite}
										disabled={isSubmitting}
										styles={[styles.addSurveyLink]}
									>
										+ Add Website
									</Button>
									{formErrors.webSearchDomains && (
										<div className={css(styles.errorMessage)}>{formErrors.webSearchDomains}</div>
									)}
								</div>
							</div>

							{/* Attachments */}
							<AIAgentTemplateAttachments
								attachments={formData.attachments}
								onChange={handleAttachmentsChange}
								templateId={!isCreateMode && existingTemplate ? existingTemplate.id : undefined}
								impersonationContext={accountImpersonationContext}
							/>
						</form>
					</TabContent>
					<TabContent value='chat-builder' className={css(styles.leftTabContent, styles.leftTabContentAIBuilder)}>
						<React.Suspense fallback={<LoadingSpinner />}>
							<ChatConversationBuilder
								impersonationContext={userImpersonationContext}
								isTemplateUnconfigured={!existingTemplate}
								defaultWebsiteUrl={account.additionalInfo?.websiteUrl}
								accountLogoUrl={account.preferences?.logo?.url}
							/>
						</React.Suspense>
					</TabContent>
				</div>

				{/* Right Panel - Preview */}
				<div className={css(styles.previewPanel)}>
					<TabRoot value={editorMode} onValueChange={value => setEditorMode(value as typeof editorMode)}>
						<TabList>
							<TabTrigger value='standalone'>Standalone</TabTrigger>
							<TabTrigger value='embedded-widget'>Embedded Widget</TabTrigger>
						</TabList>
					</TabRoot>
					{editorMode === 'standalone' ? (
						<AssistantPreviewWidget
							name={formData.name}
							description={formData.description}
							avatarUrl={avatarPreviewUrl}
							conversationStarters={conversationStarterStrings}
						/>
					) : (
						<EmbeddedPreviewWidget
							name={formData.name}
							description={formData.description}
							avatarUrl={avatarPreviewUrl}
							conversationStarters={conversationStarterStrings}
							position={embedPosition}
							launcherLabel={embedLauncherLabel}
							primaryColor={embedPrimaryColor || undefined}
							foregroundColor={effectiveForegroundColor}
						/>
					)}
				</div>

				{/* Delete Confirmation Dialog */}
				<Dialog
					open={showDeleteConfirmation}
					onOpenChange={open => !open && !deleteMutation.isPending && setShowDeleteConfirmation(false)}
					dismissOnOutsideClick={!deleteMutation.isPending}
				>
					<DialogPopup>
						<DialogHeader>
							<DialogTitle>Delete Customer Assistant?</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete &quot;{existingTemplate?.name}&quot;? This action cannot be undone.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								onClick={() => setShowDeleteConfirmation(false)}
								variant='secondary'
								disabled={deleteMutation.isPending}
							>
								Cancel
							</Button>
							<Button onClick={handleDeleteConfirm} variant='destructive-secondary' disabled={deleteMutation.isPending}>
								{deleteMutation.isPending ? 'Deleting...' : 'Delete'}
							</Button>
						</DialogFooter>
					</DialogPopup>
				</Dialog>
			</div>
		</TabRoot>
	);
}

const ASSISTANT_NAME = 'CustomAgentBuilderAgent';

function ChatConversationBuilder({
	impersonationContext,
	isTemplateUnconfigured,
	defaultWebsiteUrl,
	accountLogoUrl,
}: {
	impersonationContext?: Api.IImpersonationContext;
	isTemplateUnconfigured: boolean;
	defaultWebsiteUrl?: string;
	accountLogoUrl?: string;
}) {
	const id = React.useId();
	const queryClient = useQueryClient();
	const conversationRef = React.useRef<StickToBottomContext>(null);
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);
	const { data } = useAIConversationFilterSuspenseQuery({
		filter: {
			criteria: [
				{
					property: Api.AIConversationFilterProperty.ConversationType,
					value: Api.AIConversationType.CustomAgentBuilder,
				},
			],
		},
		impersonationContext,
	});
	const latestConversationId = data?.[0]?.id;
	const createConversationMutation = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: () => {
			toast.error('Failed to create conversation');
		},
	});
	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(latestConversationId),
		id: latestConversationId,
		impersonationContext,
	});
	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: ASSISTANT_NAME,
		onSuccess: () => {
			invalidateAIAgentTemplateFilter();
		},
	});
	const createConversation = createConversationMutation.mutate;
	const attachmentsController = useAIChatAttachments({ impersonationContext });
	const { uploadedById, isUploading, reset: resetAttachments } = attachmentsController;
	const handlePromptSubmit = ({ text }: PromptInputMessage) => {
		if (!text.trim()) {
			return;
		}
		if (isUploading) {
			toast.warning('Please wait for the uploads to finish.');
			return;
		}
		if (aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming) {
			toast.warning('Please wait for the current response to finish.');
			return;
		}
		const fileContents: Api.AIFileContent[] = Object.values(uploadedById).map(a => ({
			_type: 'AIFileContent',
			fileAttachmentId: a.id,
		}));
		const contents: Api.AIChatContent[] = [{ _type: 'AITextContent', text } as Api.AITextContent, ...fileContents];
		conversationRef.current?.scrollToBottom();
		removeErrorMessageFromConversationCache(latestConversationId, impersonationContext);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: latestConversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, contents, id: null } as Api.AIChatMessage,
			]
		);
		aiAssistantStreamingMutation.mutate({
			impersonationContext,
			conversationId: latestConversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						contents,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
		});
		resetAttachments();
	};
	const handleStarterFormSubmit = ({ websiteUrl, useAccountLogo, instructions }: AgentBuilderStarterFormSubmission) => {
		const lines = ["Set up the client's website chatbot."];
		if (websiteUrl) {
			lines.push(`Website: ${websiteUrl.replace(/[\r\n\t]/g, ' ').trim()}`);
		}
		if (useAccountLogo) {
			lines.push("Avatar: use the account's logo on file as the chatbot avatar.");
		} else if (accountLogoUrl) {
			lines.push('Avatar: do not use the account logo — leave the avatar unset for now.');
		}
		if (instructions) {
			lines.push(`Operator instructions: ${instructions.replace(/[\r\t]/g, ' ')}`);
		}
		if (Object.keys(uploadedById).length > 0) {
			lines.push('Materials attached — use them to sharpen the setup.');
		}
		handlePromptSubmit({ text: lines.join('\n'), files: [] });
	};
	React.useEffect(() => {
		if (data.length === 0) {
			createConversation({
				assistantName: ASSISTANT_NAME,
				impersonationContext,
			});
		}
	}, [data, createConversation, impersonationContext]);
	if (!latestConversationId || !aiConversationChatMessageQuery.isSuccess) {
		return <LoadingSpinner />;
	}
	const showStarterForm = aiConversationChatMessageQuery.data?.length === 0 && isTemplateUnconfigured;
	return (
		<>
			<ChatConversation styles={[styles.conversation]} contextRef={conversationRef}>
				<ChatConversationContent>
					{showStarterForm ? (
						<AIChatAssistantMessage>
							Hi! Let's set up the client's assistant. Confirm the details below and hit Get Started — I'll research
							their website and come back with a complete setup for you to review and approve.
							<AgentBuilderStarterForm
								defaultWebsiteUrl={defaultWebsiteUrl}
								accountLogoUrl={accountLogoUrl}
								disabled={aiAssistantStreamingMutation.isLoading || isUploading}
								attachmentsController={attachmentsController}
								onGetStarted={handleStarterFormSubmit}
							/>
						</AIChatAssistantMessage>
					) : (
						<AIChatAssistantMessage>
							Hi! Tell me what to change about the client's assistant, and I'll help you configure it. You can also
							upload files to provide additional context.
						</AIChatAssistantMessage>
					)}
					{aiConversationChatMessageQuery.data?.map((message, i) => (
						<AIConversationMessage
							key={`${id}-message-${message.id ?? i}`}
							conversationId={latestConversationId}
							message={message}
							showMessageToolbar={false}
							impersonationContext={impersonationContext}
						/>
					))}
					{aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage>
							<LoadingSpinner type='tiny' />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			{showStarterForm ? null : (
				<PromptInput
					styles={[bs.mt4]}
					onSubmit={handlePromptSubmit}
					accept={AI_CHAT_FILE_ACCEPT}
					maxFiles={AI_CHAT_MAX_FILES}
					maxFileSize={AI_CHAT_MAX_FILE_SIZE}
					onError={err => toast.error(err.message)}
					multiple
				>
					<PromptInputAttachments controller={attachmentsController} />
					<PromptInputBody>
						<PromptInputTextarea ref={textareaRef} placeholder='' />
					</PromptInputBody>
					<PromptInputFooter>
						<PromptInputTools>
							<PromptInputAttachmentButton />
							<PromptInputSpeechButton textareaRef={textareaRef} />
						</PromptInputTools>
						<PromptInputSubmit status={aiAssistantStreamingMutation.status} disabled={isUploading} />
					</PromptInputFooter>
				</PromptInput>
			)}
		</>
	);
}
